import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaTicketAlt,
  FaCheckCircle,
  FaTimesCircle,
  FaUser,
} from "react-icons/fa";
import LoadingPage from "../../components/Loading/LoadingPage";
import { useAuth } from "../../contexts/AuthContext";
import { getTicketByCode, requestRefund, isRefundEligible } from "../../services/ticketingService";

import "./TicketDetail.css";

/**
 * Full-screen ticket detail with QR code.
 * Route: /tickets/:ticketCode
 */
export default function TicketDetail() {
  const { ticketCode } = useParams();
  const navigate = useNavigate();
  const { customerToken } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refundConfirming, setRefundConfirming] = useState(false);
  const [refunding, setRefunding] = useState(false);
  const [refundSuccess, setRefundSuccess] = useState(false);
  const [refundError, setRefundError] = useState("");
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    if (!refundSuccess) return;
    setCountdown(5);
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          navigate("/");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [refundSuccess, navigate]);

  useEffect(() => {
    if (!ticketCode) {
      setError("Ticket not found.");
      setLoading(false);
      return;
    }

    let mounted = true;
    (async () => {
      try {
        const data = await getTicketByCode(ticketCode);
        if (mounted) {
          setTicket(data);
          setLoading(false);
        }
      } catch {
        if (mounted) {
          setError("Ticket not found or invalid.");
          setLoading(false);
        }
      }
    })();
    return () => { mounted = false; };
  }, [ticketCode]);

  if (loading) return <LoadingPage />;

  if (error || !ticket) {
    return (
      <div className="td-page"><div className="td-container">
        <div className="td-error">
          <FaTimesCircle className="td-error-icon" />
          <p>{error || "Ticket not found."}</p>
        </div>
      </div></div>
    );
  }

  const statusConfig = {
    valid: { label: "Valid", className: "td-status--valid", Icon: FaCheckCircle, desc: "This ticket is valid for entry." },
    used: { label: "Used", className: "td-status--used", Icon: FaCheckCircle, desc: "This ticket has been used." },
    cancelled: { label: "Cancelled", className: "td-status--cancelled", Icon: FaTimesCircle, desc: "This ticket has been cancelled." },
  };

  const config = statusConfig[ticket.status] || statusConfig.valid;
  const StatusIcon = config.Icon;

  const TZ = "America/Chicago";

  const eventDate = ticket.start_at
    ? new Date(ticket.start_at).toLocaleDateString("en-US", {
        weekday: "long", month: "long", day: "numeric", year: "numeric", timeZone: TZ,
      })
    : "TBD";

  const fmtTime = (dt) => dt
    ? new Date(dt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: TZ })
    : null;

  // Show valid_from–valid_until from the tier; fall back to event start_at/end_at for missing bound
  const eventTime = (() => {
    const vf = fmtTime(ticket.valid_from) || fmtTime(ticket.start_at);
    const vu = fmtTime(ticket.valid_until) || fmtTime(ticket.end_at);
    if (vf && vu && vf !== vu) return `${vf} – ${vu}`;
    return vf || vu || "";
  })();

  const checkedInTime = ticket.checked_in_at
    ? new Date(ticket.checked_in_at).toLocaleTimeString("en-US", {
        hour: "numeric", minute: "2-digit", hour12: true, timeZone: TZ,
      })
    : null;

  const isInactive = ticket.status === "cancelled" || ticket.reservation_payment_status === "refunded";
  const canRefund = (customerToken || ticket.attendee_email) && isRefundEligible(ticket);

  const handleRefund = async () => {
    setRefunding(true);
    setRefundError("");
    try {
      await requestRefund(ticket.reservation_id, {
        customerToken: customerToken || null,
        guestEmail: customerToken ? null : (ticket.attendee_email || null),
      });
      setRefundSuccess(true);
      setRefundConfirming(false);
      setTicket((prev) => ({ ...prev, status: "cancelled", reservation_payment_status: "refunded" }));
    } catch (err) {
      setRefundError(err.message || "Refund failed. Please try again.");
    } finally {
      setRefunding(false);
    }
  };

  return (
    <>
    <div className="td-page">
    <div className="td-container">
      <div className="td-card">
        {/* Status banner */}
        <div className={`td-status-banner ${config.className}`}>
          {StatusIcon && <StatusIcon className="td-status-icon" />}
          <div>
            <strong>{config.label}</strong>
            <span>{config.desc}</span>
            {checkedInTime && (
              <span className="td-checkin-time">Checked in at {checkedInTime}</span>
            )}
          </div>
        </div>

        {/* Event info */}
        <div className="td-event-section">
          <h1 className="td-event-title">{ticket.event_title}</h1>
          <div className="td-event-meta">
            <div className="td-meta-row">
              <FaCalendarAlt />
              <span>{eventDate}{eventTime && ` · ${eventTime}`}</span>
            </div>
            {ticket.venue_name && (
              <div className="td-meta-row">
                <FaMapMarkerAlt />
                <span>{ticket.venue_name}</span>
              </div>
            )}
          </div>
        </div>

        {/* Ticket details */}
        <div className="td-ticket-info">
          <div className="td-info-row">
            <FaTicketAlt className="td-info-icon" />
            <div>
              <span className="td-info-label">Ticket Type</span>
              <span className="td-info-value">{ticket.tier_name}</span>
            </div>
          </div>
          {ticket.attendee_name && (
            <div className="td-info-row">
              <FaUser className="td-info-icon" />
              <div>
                <span className="td-info-label">Attendee</span>
                <span className="td-info-value">{ticket.attendee_name}</span>
              </div>
            </div>
          )}
        </div>

        {/* QR Code — only for active tickets */}
        {!isInactive && (
          <div className="td-qr-section">
            <h2 className="td-qr-label">Scan at the Door</h2>
            {ticket.qr_image_url ? (
              <div className="td-qr-wrap">
                <img
                  src={ticket.qr_image_url}
                  alt="Ticket QR Code"
                  className="td-qr-image"
                />
              </div>
            ) : (
              <div className="td-qr-placeholder">
                <FaTicketAlt />
                <p>QR code is being generated…</p>
                <small>Refresh the page in a moment.</small>
              </div>
            )}

            <p className="td-ticket-code-label">
              Ticket ID: <code className="td-ticket-code">{String(ticket.ticket_code).slice(0, 8).toUpperCase()}…</code>
            </p>
          </div>
        )}

        {/* Screenshot note */}
        {ticket.status === "valid" && (
          <div className="td-screenshot-note">
            Screenshot this ticket or keep this page open at the door.
          </div>
        )}

        {/* Refund section */}
        {refundSuccess && (
          <div className="td-refund-success">
            <FaCheckCircle />
            <span>Refund initiated. You'll receive a confirmation email shortly.</span>
          </div>
        )}

        {/* Countdown redirect bar */}
        {refundSuccess && countdown !== null && (
          <div className="td-redirect-bar">
            <div className="td-redirect-bar-inner">
              <div className="td-redirect-progress" style={{ animationDuration: "5s" }} />
              <span className="td-redirect-text">
                Redirecting to home in <strong>{countdown}</strong>s…
              </span>
              <button className="td-redirect-now" onClick={() => navigate("/")}>
                Go now
              </button>
            </div>
          </div>
        )}

        {canRefund && !refundSuccess && (
          <div className="td-refund-section">
            <button
              className="td-refund-btn"
              onClick={() => { setRefundConfirming(true); setRefundError(""); }}
            >
              Can't make it? Request a refund.
            </button>
          </div>
        )}
      </div>
    </div>
    </div>

    {/* Refund confirmation modal */}
    {refundConfirming && (
      <div className="td-modal-overlay" onClick={() => { if (!refunding) { setRefundConfirming(false); setRefundError(""); } }}>
        <div className="td-modal" onClick={(e) => e.stopPropagation()}>
          <h3 className="td-modal-title">Request a Refund</h3>
          <p className="td-modal-body">
            This will cancel all tickets in this order and refund your payment.
          </p>
          {refundError && <p className="td-refund-error">{refundError}</p>}
          <div className="td-modal-btns">
            <button
              className="td-refund-confirm-yes"
              onClick={handleRefund}
              disabled={refunding}
            >
              {refunding ? "Processing…" : "Yes, refund my tickets"}
            </button>
            <button
              className="td-refund-confirm-no"
              onClick={() => { setRefundConfirming(false); setRefundError(""); }}
              disabled={refunding}
            >
              Keep my tickets
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
